// ============================================================
// SocialNotifications — transient social UI overlay
// ============================================================
//
// Two responsibilities:
//   1. `attachPendingBadge(scene, anchor)` — small red numbered pip that tracks
//      SocialService.getPendingCount() for the Social button in the current scene.
//   2. `attachFriendOnlineToasts(scene)` — listens for `friend-online` updates
//      and shows a small transparent popup that fades out after ~4.5s.
//
// Both helpers return a cleanup function; scenes call it on shutdown/destroy.

import Phaser from 'phaser';
import type { SocialUpdate } from '@roe/shared';
import { SocialService } from '../services/SocialService.js';

const TOAST_LIFESPAN_MS = 4500;
const TOAST_FADE_MS = 420;
const TOAST_W = 220;
const TOAST_H = 28;
const TOAST_GAP = 6;
const TOAST_DEPTH = 9000;

/** Red pending-request pip attached to a social button. Updates live whenever
 *  the server pushes a new pending-changed event. */
export function attachPendingBadge(
  scene: Phaser.Scene,
  anchor: { x: number; y: number },
): () => void {
  const ss = SocialService.getInstance();
  ss.ensureWired();

  const container = scene.add.container(anchor.x, anchor.y).setDepth(30);
  const circle = scene.add.circle(0, 0, 7, 0xc83030, 1)
    .setStrokeStyle(1, 0xffd080, 1);
  const text = scene.add.text(0, 0, '', {
    fontSize: '9px', fontFamily: 'Georgia, serif', color: '#fff4d8', fontStyle: 'bold',
  }).setOrigin(0.5);
  container.add([circle, text]);

  const render = (): void => {
    const n = ss.getPendingCount();
    if (n <= 0) {
      container.setVisible(false);
      return;
    }
    container.setVisible(true);
    text.setText(n > 9 ? '9+' : String(n));
  };
  render();

  const unsub = ss.onUpdate((u: SocialUpdate) => {
    if (u.kind === 'pending-changed') render();
  });

  const cleanup = (): void => {
    unsub();
    container.destroy();
  };
  scene.events.once('shutdown', cleanup);
  scene.events.once('destroy', cleanup);
  return cleanup;
}

/** Queue of visible toasts so we can stack them vertically. */
interface ActiveToast {
  container: Phaser.GameObjects.Container;
  bornAt: number;
}

/** Listens for friend-online events and shows a small transparent toast in the
 *  top-center of the scene. Auto-dismisses after ~4.5s; stacks if several fire
 *  in quick succession. */
export function attachFriendOnlineToasts(scene: Phaser.Scene): () => void {
  const ss = SocialService.getInstance();
  ss.ensureWired();

  const active: ActiveToast[] = [];
  const w = scene.scale.width;

  const reflow = (): void => {
    active.forEach((t, i) => {
      const targetY = 56 + i * (TOAST_H + TOAST_GAP);
      scene.tweens.add({ targets: t.container, y: targetY, duration: 180, ease: 'Quad.easeOut' });
    });
  };

  const show = (name: string): void => {
    const container = scene.add.container(w / 2, 56).setDepth(TOAST_DEPTH).setAlpha(0);
    const bg = scene.add.rectangle(0, 0, TOAST_W, TOAST_H, 0x0a0608, 0.55)
      .setStrokeStyle(1, 0x6a8a40, 0.7);
    const dot = scene.add.circle(-TOAST_W / 2 + 14, 0, 3.5, 0x6aaa60, 1);
    const label = scene.add.text(-TOAST_W / 2 + 24, 0, `${name} is online`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8e8a0',
    }).setOrigin(0, 0.5);
    container.add([bg, dot, label]);

    const entry: ActiveToast = { container, bornAt: Date.now() };
    active.unshift(entry);
    reflow();

    // Fade-in, hold, fade-out.
    scene.tweens.add({ targets: container, alpha: 0.85, duration: 260, ease: 'Quad.easeOut' });
    scene.time.delayedCall(TOAST_LIFESPAN_MS - TOAST_FADE_MS, () => {
      if (!container.scene) return;
      scene.tweens.add({
        targets: container, alpha: 0, duration: TOAST_FADE_MS,
        onComplete: () => {
          const idx = active.indexOf(entry);
          if (idx >= 0) active.splice(idx, 1);
          container.destroy();
          reflow();
        },
      });
    });
  };

  const unsub = ss.onUpdate((u: SocialUpdate) => {
    if (u.kind !== 'friend-online') return;
    const name = (u.name ?? '').trim();
    if (!name) return;
    show(name);
  });

  const cleanup = (): void => {
    unsub();
    for (const t of active) t.container.destroy();
    active.length = 0;
  };
  scene.events.once('shutdown', cleanup);
  scene.events.once('destroy', cleanup);
  return cleanup;
}
